import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import api from '../lib/api';
import { useAuth } from './AuthContext';

/**
 * Single-source-of-truth for Storybook build status across the
 * super-admin UI. One in-flight `/admin/storybook/status` request,
 * regardless of how many components subscribe (header pill, admin
 * page popover, future widgets).
 *
 * Polling cadence:
 *   - SLOW (30 s): default while the bundle is healthy / idle.
 *   - FAST (3 s): activates whenever
 *       (a) a subscriber calls `boost()` (e.g. popover is open), or
 *       (b) the last status reported `rebuilding: true`.
 *   - DISABLED: when the user is not a super-admin or not signed in.
 *     This means login / public pages never poll.
 */
const SLOW_MS = 30000;
const FAST_MS = 3000;

const StorybookStatusContext = createContext({
  status: null,
  refresh: async () => {},
  boost: () => () => {},
});

export function StorybookStatusProvider({ children }) {
  const { user, isSuperAdmin, userRole } = useAuth();
  const [status, setStatus] = useState(null);
  const inFlight = useRef(null);
  const boostCount = useRef(0);
  const timerRef = useRef(null);

  // Pill is shown to super-admins AND org-admins. Plain users never poll.
  const enabled = Boolean(user) && (isSuperAdmin || userRole === 'org_admin');

  const refresh = useCallback(async () => {
    if (!enabled) return null;
    if (inFlight.current) return inFlight.current;
    const promise = (async () => {
      try {
        const { data } = await api.get('/admin/storybook/status');
        setStatus(data);
        return data;
      } catch {
        return null;
      } finally {
        inFlight.current = null;
      }
    })();
    inFlight.current = promise;
    return promise;
  }, [enabled]);

  // boost() returns an "unboost" cleanup that the caller MUST invoke
  // (on unmount or on close) so polling drops back to slow cadence.
  const boost = useCallback(() => {
    boostCount.current += 1;
    rescheduleRef.current?.();
    return () => {
      boostCount.current = Math.max(0, boostCount.current - 1);
      rescheduleRef.current?.();
    };
  }, []);

  // Reschedule polling based on current state. We hold the function
  // in a ref so `boost()` can call it without participating in the
  // useEffect dep array.
  const rescheduleRef = useRef(null);
  useEffect(() => {
    rescheduleRef.current = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (!enabled) return;
      const wantsFast = boostCount.current > 0 || status?.rebuilding;
      const delay = wantsFast ? FAST_MS : SLOW_MS;
      timerRef.current = setTimeout(async () => {
        await refresh();
        rescheduleRef.current?.();
      }, delay);
    };
    rescheduleRef.current();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [enabled, status?.rebuilding, refresh]);

  // Initial fetch when the user is identified as a super-admin.
  useEffect(() => {
    if (enabled) refresh();
  }, [enabled, refresh]);

  // Reset state when the user signs out (so the next super-admin
  // login starts clean).
  useEffect(() => {
    if (!enabled) {
      setStatus(null);
    }
  }, [enabled]);

  const value = useMemo(
    () => ({ status, refresh, boost }),
    [status, refresh, boost],
  );

  return (
    <StorybookStatusContext.Provider value={value}>
      {children}
    </StorybookStatusContext.Provider>
  );
}

export const useStorybookStatus = () => useContext(StorybookStatusContext);
